import gql from 'graphql-tag'

// enums
export const PortfolioType = gql`
    enum PortfolioType {
        GENERAL_INVESTMENT_ACCOUNT
        ISA
        SIPP
        JISA
    }
`;
export const ClientServiceType = gql`
    enum ClientServiceType {
        APP
        ADVISED
        SELF_SELECT
        STAFF
    }
`;
export const ClientSourceType = gql`
    enum ClientSourceType {
        GUIDED
        SAGE
        LSEG
    }
`;


// Create Portfolio
export const gqlCreatePortfolio = gql`
    mutation CreatePortfolio(
        $type: PortfolioType!, 
        $amount: Float!,
        $service: ClientServiceType,
        $corporateId: ID
    ) { 
        createPortfolio(
            portfolioInput: {
                targetValue: $amount,
                type: $type, 
                service: $service,
                corporateId: $corporateId
            }
        ) {
            id
            type
        }
    }
`

// Get Portfolios
export const gqlGetPortfolios = gql`
    query Portfolios {
        clientInfo {
            id
            firstName
            externalReference
            status
            targetRetirementAge
            maritalStatus
            employmentStatus
            annualIncome
            portfolios(page: 0, size: 100) {
                elements {
                    id
                    type
                    service
                    source
                    depositValue
                    withdrawnValue
                    targetValue
                    unprocessedDepositValue
                    currentTaxYearData {
                        depositValue
                        depositLimit
                        lastDay
                        firstDay
                    }
                    sendingMoneyAllowed
                    shortName
                    liveData {
                        currentValue
                        cashBalance
                        assets {
                            name
                            holdings
                            price
                            value
                            security {
                                id
                                name
                                marketRegion
                            }
                        }
                    }
                    funds(
                        page: 0
                        size: 100
                    ) {
                        elements {
                            id
                            name
                            ongoingFees
                        }
                    }
                    securityAllocations {
                        id
                        createdAt
                        allocations {
                            security {
                                id
                                name
                                longDescriptionLink
                                ongoingFees
                            }
                            allocation
                            available
                        }
                    }
                    activeWithdrawal {
                        id
                    }
                    wbsReference
                    allocationManagementAllowed
                    transactions (page: 0, size: 100) {
                        elements {
                            id
                            status
                            type
                            deductionLimitsEffect
                            source
                            amount
                            externalReference
                            wbsReference
                            portfolioId
                            createdAt
                        }
                    }
                    corporate {
                        id
                        name
                        activeShareTransferScheme {
                            id
                            name
                            active
                            startDate
                            endDate
                            maturityDate
                        }
                        payrollContributionScheme {
                            id
                            active
                        }
                    }
                    diversificationAllowed
                    activeFundSwitchRequests {
                        id
                    }
                    createdAt
                    paymentsAvailability
                    defaultFund {
                        id
                        name
                        ongoingFees
                    }
                    beneficiariesAllowed
                    performanceAllowed
                    activeShareTransferRequests {
                        id
                        status
                    }
                }
            }
            address {
                street
                town
                postCode
                country
                buildingNumber
                flatNumber
                buildingName
            }
            payrollContribution {
                id
                status
            }
        }
    }
`

// Get Portfolio
export const gqlGetPortfolio = gql`
    query Portfolio($portfolioId: ID!) {
        clientInfo {
            id
            status
            targetRetirementAge
            portfolio(id: $portfolioId) {
                id
                type
                service
                source
                depositValue
                withdrawnValue
                targetValue
                shortName
                status
                wbsReference
                allocationManagementAllowed
                unprocessedDepositValue
                activeWithdrawal {
                    id
                    status
                    amount
                    isFullAmount
                    createdAt
                    requestedAt
                    expectedToBeRequestedAt
                    processedAt
                }
                currentTaxYearData {
                    depositValue
                    depositLimit
                    lastDay
                    firstDay
                }
                securityAllocations {
                    id
                    createdAt
                    allocations {
                        security {
                            id
                            name
                            longDescriptionLink
                            ongoingFees
                        }
                        allocation
                        available
                    }
                }
                defaultFund {
                    id
                    name
                    longDescriptionLink
                    ongoingFees
                }
                beneficiariesAllowed
                beneficiaries {
                    id
                    percentage
                    entity {
                        ... on PersonBeneficiaryEntity {
                            firstName
                            lastName
                            birthdate
                            relationship
                            address
                        }
                        ... on CharityBeneficiaryEntity {
                            name
                            ukRegisteredNumber
                        }
                    }
                }
                paymentsAvailability
                dividendReinvestmentStatus
            }
            type
            payrollContribution {
                status
            }
            services {
                type
                corporate {
                    id
                    name
                    shortName
                    security {
                        id
                        name
                    }
                    feePercentage
                    activeShareTransferScheme {
                        name
                        active
                    }
                    payrollContributionScheme {
                        active
                    }
                }
            }
        }
    }
`

// Get Portfolio Holdings
export const gqlGetPortfolioHoldings = gql`
    query PortfolioHoldings($portfolioId: ID!) {
        clientInfo {
            id
            status
            portfolio(id: $portfolioId) {
                id
                type
                service
                source
                shortName
                status
                allocationManagementAllowed
                unprocessedDepositValue
                liveData {
                    currentValue
                    cashBalance
                    assets {
                        name
                        holdings
                        price
                        value
                        security {
                            id
                            name
                            marketRegion
                        }
                    }
                }
            }
        }
    }
`

// Get Portfolio Transactions (EOS 'Transactions' === VL 'Trades')
export const gqlGetPortfolioTransactions = gql`
    query PortfolioTransactions($portfolioId: ID!, $page: Int!, $size: Int!) {
        clientInfo {
            id
            status
            portfolio(id: $portfolioId) {
                id
                type
                service
                source
                shortName
                status
                trades(size: $size, page: $page) {
                    pageInfo {
                        totalElements
                        totalPages
                        nextPageNumber
                    }
                    elements {
                        tradeDate
                        settlementDate
                        type
                        currency
                        amount
                        price
                        quantity
                        isin
                        epic
                        securityName
                    }
                }
                wbsReference
            }
        }
    }
`

// Get Portfolio Payments (VL 'Transactions' === EOS Payments)
export const gqlGetPortfolioPayments = gql`
    query PortfolioPayments($portfolioId: ID!, $page: Int!, $size: Int!) {
        clientInfo {
            id
            status
            portfolio(id: $portfolioId) {
                id
                type
                service
                shortName
                status
                trades(size: $size, page: $page) {
                    pageInfo {
                        totalElements
                        totalPages
                        nextPageNumber
                    }
                    elements {
                        tradeDate
                        settlementDate
                        type
                        currency
                        amount
                        price
                        quantity
                        isin
                        epic
                        securityName
                    }
                }
                wbsReference
            }
        }
    }
`

export const gqlGetPortfolioPerformance = gql`
    query PortfolioPerformance($portfolioId: ID!, $dateRange: PortfolioPerformanceDateRange!) {
        clientInfo {
            id
            portfolio(id: $portfolioId) {
                id
                performance (dateRange: $dateRange) {
                    historyDates
                    historyValues
                }
            }
        }
    } 
`

// Update Dividend Management Status
export const gqlUpdateDividendManagementStatus = gql`
        mutation UpdatePortfolioDividendReinvestment(
        $portfolioId: ID!, 
        $dividendReinvestmentEnabled: Boolean!
    ) {
        updatePortfolioDividendReinvestment(
            portfolioId: $portfolioId
            dividendReinvestmentEnabled: $dividendReinvestmentEnabled
        ) {
            id
            shortName
            dividendReinvestmentStatus
        }
    }
`